/**
 * ConnectHero Component
 * Hero section with breadcrumb, heading, subtitle, personalization banner,
 * and community/ritual carousel
 */

import { ChevronRight, MapPin, MoreVertical, Users } from "lucide-react"
import { memo } from "react"
import { Link, useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { Carousel } from "@/components/carousel/Carousel"
import { formatLocation } from "@/components/shared/formatLocation"
import { PersonalizationBanner } from "@/components/shared/PersonalizationBanner"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

import type { ConnectCommunity } from "../types/connect.types"

interface ConnectHeroProps {
  communities: ConnectCommunity[]
}

export const ConnectHero = memo(function ConnectHero({ communities }: ConnectHeroProps) {
  return (
    <section className="mb-10 md:mb-8">
      {/* Breadcrumb */}
      <div className="md:pt-6 max-w-360 mx-auto px-5 md:px-17.5">
        <nav
          className="text-[13px] text-text-primary font-monteserat mb-2 gap-1 hidden md:flex items-center"
          aria-label="Breadcrumb"
        >
          <Link to="/" className="hover:opacity-80 transition-opacity">
            Home
          </Link>
          <ChevronRight size={17} />
          <span>Connect</span>
        </nav>

        {/* Heading */}
        <h1 className="text-xl font-semibold leading-normal md:section-heading md:text-[30px] text-text-primary/90 md:mt-9">
          Connect
        </h1>
        <p className="text-[14px] text-text-primary/90 mt-1.25 md:mt-3">
          Join a space where rituals bring people together.
        </p>

        {/* Personalization Banner */}
        <PersonalizationBanner className="mb-7.5 mt-4.5 md:mt-7" />
      </div>

      {/* Community Carousel */}
      {communities.length > 0 && (
        <Carousel
          data={communities}
          renderItem={community => <HeroCarouselCard community={community} />}
          itemWidth="min(656px, 100%)"
          contentClassName="h-[260px] md:h-[600px]"
        />
      )}
    </section>
  )
})

function HeroCarouselCard({ community }: { community: ConnectCommunity }) {
  const location = formatLocation(community.city, community.state)
  const navigate = useNavigate()
  const communityUrl = `${window.location.origin}/communities/${community.id}`

  const handleCopyLink = () => {
    navigator.clipboard.writeText(communityUrl)
    toast.success("Link copied to clipboard")
  }

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: community.name, url: communityUrl })
      } catch {
        // User cancelled or share failed — fall back to copy
        handleCopyLink()
      }
    } else {
      handleCopyLink()
    }
  }

  return (
    <Link
      to={`/communities/${community.id}`}
      className="block relative aspect-[16/11] w-full overflow-hidden rounded-[20px] group"
    >
      {/* Cover image */}
      {community.coverImage ? (
        <img
          src={community.coverImage}
          alt={community.name}
          className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
      ) : (
        <div className="absolute inset-0 bg-card" />
      )}

      {/* Top-right action button */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            onClick={e => {
              e.preventDefault()
              e.stopPropagation()
            }}
            className="absolute top-3 right-3 z-10 w-[44px] h-[44px] flex items-center justify-center bg-connect-glass-bg border border-connect-glass-border rounded-[10px] cursor-pointer hover:bg-white/10 transition-colors"
            aria-label="More options"
          >
            <MoreVertical className="w-5 h-5 text-text-primary" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={e => e.stopPropagation()}>
          <DropdownMenuItem onClick={handleCopyLink}>Copy Link</DropdownMenuItem>
          <DropdownMenuItem onClick={handleShare}>Share</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={e => {
              e.preventDefault()
              navigate(`/communities/${community.id}`)
            }}
          >
            View Community
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Bottom gradient overlay with backdrop blur */}
      <div className="absolute bottom-0 left-0 right-0 h-[113px] bg-gradient-to-t from-[rgba(0,0,0,0.7)] to-[rgba(0,0,0,0)]]" />
      {/* Content */}
      <div className="absolute bottom-0 left-0 right-0 px-4 pb-4">
        <h3 className="text-[14px] md:text-[18px] font-semibold text-text-primary opacity-90 mb-1.5 leading-tight">
          {community.name}
        </h3>
        <div className="flex items-center gap-[10px] text-[13px] text-text-primary opacity-80 mb-1">
          <span className="flex items-center gap-1">
            <Users className="w-3.5 h-3.5" />
            {community.memberCount} members
          </span>
          <span>{community.ritualCount} Rituals</span>
        </div>
        {location && (
          <p className="flex items-center gap-1 text-[13px] text-text-primary opacity-60">
            <MapPin className="w-3.5 h-3.5" />
            {location}
          </p>
        )}
      </div>
    </Link>
  )
}
